import { Button, Table } from '@boozallen-uip/uswds-react-core';
import { IndeterminateSpinner } from '@boozallen-uip/uswds-react-loading';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import head from 'lodash/head';
import validateHyperlink, { formatUTCDisplayDate } from '../../../utlities/common.funcs';
import ExternalLinkIcon from '../../../components/ExternalLinkIcon.component';
import {
  PRODUCT_VISIBILITY_PUBLIC,
  PRODUCT_VISIBILITY_BUYERS_ONLY,
  PRODUCT_VISIBILITY_HIDE,
  VENDOR_OVERVIEW_URL
} from '../../../utlities/core.constants';
import { UserContext } from '../../UserContext';

export default function ProductListTable({
  products,
  isLoading,
  emptyMessage,
  onRequestAcr,
  acrAvailability,
  acrAddedOn,
  selectableIds,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) {
  const {user, isVendorUser, isAgencyUser} = useContext(UserContext);
  // Columns: select, Product, Category, PSC, Vendor, (Visibility for vendors), Product ACR, Date.
  const columnCount = isVendorUser ? 8 : 7;
  const linkClasses = "rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-cool";

  const selectableIdSet = new Set(selectableIds.map(String));
  const selectedIdSet = new Set(selectedIds.map(String));
  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIdSet.has(String(id)));

  // The date the ACR was added. An in-site ACR uses the report date (that is the ACR this row
  // links to). Otherwise fall back to the external link's added date, then its authored date.
  const getDateAcrAdded = (product) => {
    if (acrAvailability[product.id] === true) {
      const inSiteAdded = formatUTCDisplayDate(acrAddedOn?.[String(product.id)]);
      if (inSiteAdded) {
        return inSiteAdded;
      }
    }
    const link = head(product.hyperlinks);
    const added = formatUTCDisplayDate(link?.createdDate) || formatUTCDisplayDate(link?.authoredDate);
    return added || 'Not Provided';
  };

  // A single Product ACR link per row, by priority: an in-site (ACR Editor) ACR wins and links to
  // the product details page; otherwise an external ACR link (icon + opens in new window); otherwise
  // "Not Provided". Request-required external links keep their existing "Contact for ACR" state.
  const renderProductAcrCell = (product) => {
    if (acrAvailability[product.id] === true) {
      return (
        <NavLink to={`/product-details/${product.id}`} className={linkClasses}>
          View ACR
        </NavLink>
      );
    }

    const link = head(product.hyperlinks);
    if (link?.requestRequired) {
      return 'Contact for ACR';
    }
    if (link?.url) {
      return (
        <a rel="external noreferrer" target="_blank" href={validateHyperlink(link.url)} className={linkClasses}>
          View {product?.name}&apos;s ACR
          <ExternalLinkIcon />
          <span className="usa-sr-only"> (opens in new window)</span>
        </a>
      );
    }
    return 'Not Provided';
  };

  const getVisibilityValue = (product) => (
    product?.visibility
    || (product?.isPrivate ? PRODUCT_VISIBILITY_HIDE : PRODUCT_VISIBILITY_PUBLIC)
  );

  const getVisibilityLabel = (visibilityValue) => {
    if (visibilityValue === PRODUCT_VISIBILITY_BUYERS_ONLY) {
      return "Buyers-only";
    }
    if (visibilityValue === PRODUCT_VISIBILITY_HIDE) {
      return "Hidden";
    }
    return "Public";
  }

  const getVisibilityBadgeClass = (visibilityValue) => {
    if (visibilityValue === PRODUCT_VISIBILITY_BUYERS_ONLY) {
      return "bg-secondary-lighter text-secondary-darker";
    }
    if (visibilityValue === PRODUCT_VISIBILITY_HIDE) {
      return "bg-accent-warm text-white";
    }
    return "bg-accent-cool-lighter text-primary-darker";
  }

  const renderVendorValue = (product) => {
    const vendorId = product.vendor?.id;
    // Agency-owned (Buyer) products have no vendor; the owning agency is the product's org, so show
    // its name here. Fall back to "Not Provided" only when there is no owning org at all.
    const ownerName = product.vendor?.name || product.agencyName || "Not Provided";

    // Agency-owned products belonging to the current Buyer's own agency link to their agency
    // overview ("My Product Listings"). There is no public per-agency page for other agencies,
    // so those (and rows with no owning org) stay plain text - never link to /vendor/undefined.
    const isOwnAgencyProduct =
      isAgencyUser && product.agencyId && String(product.agencyId) === String(user?.agencyId);
    if (isOwnAgencyProduct) {
      return (
        <NavLink to={VENDOR_OVERVIEW_URL} className={linkClasses}>
          {ownerName}
        </NavLink>
      );
    }

    // Vendor users (viewing their own listings) and rows without a linkable vendor are plain text.
    if (isVendorUser || !vendorId) {
      return <span>{ownerName}</span>;
    }

    return (
      <NavLink to={`/vendor/${vendorId}`} className={linkClasses}>
        {ownerName}
      </NavLink>
    );
  };

  return isLoading ?
    <IndeterminateSpinner type="circle" color="blue" /> :
    <Table border={false} style={{ width: "100%" }} className='text-primary-darker'>
      <thead>
        <tr className='[&>th]:text-primary-darker'>
          <th scope="col" className="text-center">
            <input
              type="checkbox"
              className="usa-checkbox__input--bordered"
              aria-label="Select all ACRs on this page for export"
              checked={allSelectableSelected}
              disabled={selectableIds.length === 0}
              onChange={(e) => onToggleSelectAll(e.target.checked)}
            />
          </th>
          <th scope="col">Product</th>
          <th scope="col">Product Category</th>
          <th scope="col">PSC</th>
          <th scope="col">Vendor</th>
          {isVendorUser ? <th scope="col" className="text-center">Visibility</th> : null}
          <th scope="col">Product ACR</th>
          <th scope="col">Date ACR Added</th>
        </tr>
      </thead>
      <tbody>
        {products.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className="py-4 italic text-base-dark" role="status" aria-live="polite">
              {onRequestAcr ? (
                <>
                  {emptyMessage.replace(/\.$/, "")}, or{" "}
                  <Button type="button" unstyled onClick={onRequestAcr}>
                    Request an ACR
                  </Button>
                  .
                </>
              ) : emptyMessage}
            </td>
          </tr>
        ) : products.map((product) => {
          const isSelectable = selectableIdSet.has(String(product.id));
          const isSelected = selectedIdSet.has(String(product.id));
          return (
          <tr key={product.id} style={{ justifyContent: 'space-between' }}>
            <td className="text-center">
              {isSelectable ? (
                <input
                  type="checkbox"
                  className="usa-checkbox__input--bordered"
                  aria-label={`Select ${product.name} ACR for export`}
                  checked={isSelected}
                  onChange={() => onToggleSelect(product.id)}
                />
              ) : null}
            </td>
            <td className='text-primary'>
              <NavLink to={`/product-details/${product.id}`} className={linkClasses}>
                {product.name}{product.isPublished === false ? " (Draft)" : ""}
              </NavLink>
            </td>
            <td>{product.category}</td>
            <td>{product.pscCode}</td>
            <td className='text-primary'>{renderVendorValue(product)}</td>
            {isVendorUser ? (() => {
              const visibilityValue = getVisibilityValue(product);
              const visibilityLabel = getVisibilityLabel(visibilityValue);
              return (
                <td className="text-center">
                  <span
                    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${getVisibilityBadgeClass(visibilityValue)}`}
                    aria-label={`Visibility: ${visibilityLabel}`}
                  >
                    {visibilityLabel}
                  </span>
                </td>
              );
            })() : null}
            <td className='text-primary'>{renderProductAcrCell(product)}</td>
            <td>{getDateAcrAdded(product)}</td>
          </tr>
          );
        })}
      </tbody>
    </Table>
}

ProductListTable.propTypes = {
  products: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onRequestAcr: PropTypes.func,
  acrAvailability: PropTypes.object,
  acrAddedOn: PropTypes.object,
  selectableIds: PropTypes.array,
  selectedIds: PropTypes.array,
  onToggleSelect: PropTypes.func,
  onToggleSelectAll: PropTypes.func,
}

ProductListTable.defaultProps = {
  isLoading: false,
  emptyMessage: "No products found.",
  onRequestAcr: undefined,
  acrAvailability: {},
  acrAddedOn: {},
  selectableIds: [],
  selectedIds: [],
  onToggleSelect: () => {},
  onToggleSelectAll: () => {},
}
